/** @format */

import { Box, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import CloseIcon from '@material-ui/icons/Close';
import * as React from 'react';
import CancelCreateTicketModal from './cancel';
import SuccessCreateTicket from './success';
import ErrorCreateTicket from './error';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/button';
import useAuth from 'src/hooks/useAuth';
import SelectComponent from 'src/components/select';
import { useDispatch, useSelector } from 'src/store';
import { clearTicketError, createTicket, getCompanyTickets, getTickets } from 'src/slices/tickets';
import { MAX_NUMBER_OF_FILES, VALIDATE_REGEX } from 'src/constants';
import UploadField from 'src/components/upload/upload';
import UpdateTicketLoading from './updateTicketLoading';

const initPostData = {
  title: null,
  contactName: null,
  contactEmail: null,
  requestType: null,
  impact: null,
  urgency: null,
  component: null,
  tollingCategory: null,
  tollingComponent: null,
  endCustomerAccount: null,
  softwareVersion: null,
  deviceserialnumber: null,
  description: null,
  attachments: null,
  attachmentFileNames: null,
  dateOfOccurence: null,
  timeOfOccurence: null,
  project: 'Tolling'
};
const impactData = [
  { value: 'Extensive', name: 'Extensive' },
  { value: 'Significant', name: 'Significant' },
  { value: 'Moderate', name: 'Moderate' },
  { value: 'Minor', name: 'Minor' }
];
const urgencyData = [
  { value: 'Critical', name: 'Critical' },
  { value: 'High', name: 'High' },
  { value: 'Medium', name: 'Medium' },
  { value: 'Low', name: 'Low' }
];

export default function CreateTicketForTollingModal({ open, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isOtherPerson, setIsOtherPerson] = React.useState(false);
  const [cancelModalOpen, setCancelModalOpen] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [postData, setPostData] = React.useState(initPostData);
  const [validations, setValidations] = React.useState({});
  const [componentData, setComponentData] = React.useState([]);
  const [files, setFiles] = React.useState([]);
  const [fileKey, setFileKey] = React.useState(Math.random().toString(36));
  const dispatch = useDispatch();
  const { userData } = useSelector(state => state.users);
  const { updateTicketLoading, error: errorTicket } = useSelector(state => state.tickets);

  React.useEffect(() => {
    if (errorTicket) setSuccess(false);
  }, [errorTicket]);
  React.useEffect(() => {
    if (user && userData) {
      restartProcess();
      if (user.roles?.includes('admin')) setIsAdmin(true);
    }
  }, [open, user, userData]);
  React.useEffect(() => {
    if (success && !updateTicketLoading) {
      setPostData(initPostData);
    }
  }, [success, updateTicketLoading]);
  // Set component data dropdown
  React.useEffect(() => {
    switch (postData.requestType) {
      case 'Task':
        setComponentData([
          { value: 'FOTA', name: 'FOTA' },
          { value: 'Proxy UI', name: 'User accounts' }
        ]);
        break;
      case 'Incident':
      case 'Question':
      default:
        setComponentData([
          { value: 'Device', name: 'Device' },
          { value: 'Device HMI', name: 'Device HMI' },
          { value: 'Proxy UI', name: 'Proxy UI' },
          { value: 'FOTA', name: 'FOTA' },
          { value: 'Location Data LORUD', name: 'Location Data LORUD' },
          { value: 'Personalisation', name: 'Personalisation' },
          { value: 'Requests feedbacks', name: 'Requests feedbacks' },
          { value: 'Commands', name: 'Commands' },
          { value: 'Events', name: 'Events' },
          { value: 'Other', name: 'Other' }
        ]);
        break;
    }
  }, [postData.requestType]);
  // Change isOtherPerson checkbox
  React.useEffect(() => {
    setValidations({});
    if (isOtherPerson) {
      setPostData(newPostData => ({
        ...newPostData,
        endCustomerAccount: '',
        contactName: '',
        contactEmail: ''
      }));
    } else if (userData) {
      setPostData(newPostData => ({
        ...newPostData,
        endCustomerAccount: userData.company,
        contactName: userData.firstName + ' ' + userData.lastName,
        contactEmail: userData.email
      }));
    }
  }, [isOtherPerson]);

  const restartProcess = React.useCallback(() => {
    setPostData({
      ...initPostData,
      customerId: userData.company || 'B2C dummy',
      endCustomerAccount: userData.firstName + ' ' + userData.lastName,
      accountName: userData.company,
      zip: userData.address?.postalCode || '',
      contactName: userData.firstName + ' ' + userData.lastName,
      contactEmail: userData.email,
      ava_endcustomer: userData.firstName + ' ' + userData.lastName,
      ava_endcustomercontact: userData.email
    });
    setFiles([]);
    setFileKey(Math.random().toString(36));
    setValidations({});
  }, [user, userData]);
  const validate = React.useCallback(async () => {
    let _validations = {
      ...validations,
      requestType: !postData.requestType,
      component: !postData.component,
      tollingCategory: !postData.tollingCategory && postData.component === 'Device',
      title: !postData.title,
      description: !postData.description,
      deviceserialnumber: !postData.deviceserialnumber,
      impact: !postData.impact,
      urgency: !postData.urgency,
      contactName: !postData.contactName,
      contactEmail: !postData.contactEmail || !VALIDATE_REGEX.email.test(postData.contactEmail)
    };
    if (postData.attachments && validations.attachments) {
      _validations = { ..._validations, attachments: validations.attachments };
    }
    setValidations(_validations);
    return _validations;
  }, [postData, validations]);

  const updatePostData = (name, value) => {
    setPostData(newPostData => ({ ...newPostData, [name]: value }));
    switch (name) {
      case 'requestType':
        setValidations(_validations => ({ ..._validations, [name]: !value }));
        setPostData(newPostData => ({
          ...newPostData,
          component: null,
          tollingCategory: null,
          tollingComponent: null
        }));
        break;
      case 'component':
        setValidations(_validations => ({ ..._validations, [name]: !value }));
        if (value === 'Device') {
          setPostData(newPostData => ({ ...newPostData, tollingComponent: 'Other' }));
        }
        if (value !== 'Device') {
          setPostData(newPostData => ({ ...newPostData, tollingCategory: 'Other', tollingComponent: value }));
        }
        break;
      case 'contactName':
      case 'tollingCategory':
      case 'title':
      case 'description':
      case 'deviceserialnumber':
      case 'impact':
      case 'urgency':
        setValidations(_validations => ({ ..._validations, [name]: !value }));
        break;
      case 'contactEmail':
        setValidations(_validations => ({ ..._validations, [name]: !value || !VALIDATE_REGEX.email.test(value) }));
        break;
      default:
        break;
    }
  };

  const closeNewTicket = () => {
    if (!updateTicketLoading) {
      onClose(false);
      setCancelModalOpen(false);

      // avoid flash of layout
      setTimeout(() => {
        setPostData(initPostData);
        setError(false);
        setSuccess(false);
        setFiles([]);
        dispatch(clearTicketError());
      }, 300);
    }
  };
  const handleClose = () => {
    if (!updateTicketLoading) {
      if (success) closeNewTicket();
      else setCancelModalOpen(true);
    }
  };
  const handleSubmit = async () => {
    const _validations = await validate();
    if (Object.keys(_validations).length === 0 || Object.values(_validations).every(item => !item)) {
      const smy_incidentdateofoccurence = postData.dateOfOccurence
        ? postData.dateOfOccurence + (postData.timeOfOccurence ? ` ${postData.timeOfOccurence}:00` : ' 00:00:00')
        : null;
      const createTicketPostData = {
        caseType: postData.requestType,
        title: postData.title,
        description: postData.description,
        customerid: postData.customerId,
        accountName: postData.accountName,
        zip: postData.zip,
        contactName: postData.contactName || postData.ava_endcustomer,
        contactEmail: postData.contactEmail || postData.ava_endcustomercontact,
        project: postData.project,
        components: postData.tollingComponent,
        impact: postData.impact,
        urgency: postData.urgency,
        smy_tollingcategory: postData.tollingCategory,
        smy_hardwareserialnumbertolling: postData.deviceserialnumber,
        smy_firmwareversiontolling: postData.softwareVersion,
        smy_incidentdateofoccurence: smy_incidentdateofoccurence
          ? new Date(smy_incidentdateofoccurence).toISOString()
          : null,
        smy_licenseplate: '',
        attachments: postData.attachments?.length > 0 ? postData.attachments : null,
        attachmentFileNames: postData.attachmentFileNames?.length > 0 ? postData.attachmentFileNames : null,
        ava_endcustomer: isOtherPerson ? postData.ava_endcustomer : null,
        ava_endcustomercontact: isOtherPerson ? postData.ava_endcustomercontact : null
      };
      await dispatch(createTicket(createTicketPostData));
      setError(false);
      setSuccess(true);

      if (window.trackDataLayer) {
        window.trackDataLayer(
          'ticket_created',
          {
            ticket_details: {
              request_type: postData.requestType,
              impact: postData.impact,
              urgency: postData.urgency,
              ticket_scope: postData.component === 'Device' ? postData.tollingCategory : postData.requestType,
              component: postData.tollingComponent
            }
          },
          user
        );
      }

      setTimeout(() => {
        dispatch(getTickets());
        dispatch(getCompanyTickets());
      }, 1000);
    }
  };

  return (
    <>
      <Dialog
        className={`ticket-modal-dialog`}
        fullWidth
        maxWidth="md"
        open={open}
        PaperProps={{
          style: {
            backgroundColor: '#fff',
            borderRadius: 10
          }
        }}
        onClose={handleClose}
        aria-labelledby="max-width-dialog-title"
      >
        <DialogActions>
          <IconButton onClick={handleClose} disabled={updateTicketLoading}>
            <CloseIcon style={{ color: '#000' }} />
          </IconButton>
        </DialogActions>
        <DialogContent>
          <div className="create-ticket-body">
            {errorTicket ? (
              <ErrorCreateTicket closeModal={closeNewTicket} />
            ) : error ? (
              <ErrorCreateTicket closeModal={closeNewTicket} />
            ) : success ? (
              <SuccessCreateTicket closeModal={closeNewTicket} />
            ) : (
              <>
                <Typography className="create-ticket-title">{t('Tickets.newTicket')}</Typography>
                <Box className="create-ticket-box">
                  {updateTicketLoading && <UpdateTicketLoading />}
                  <div className="contact-person-heading">{t('Tickets.contactPerson')}</div>
                  {userData && (
                    <div className="contact-person">
                      <div className="contact-person-left">
                        <p className="contact-name">
                          {userData.firstName} {userData.lastName}
                        </p>
                        <p className="contact-email">{userData.email}</p>
                        <p className="contact-tel">{userData.address?.phoneNumber || ''}</p>
                      </div>
                      <div className="contact-person-right">
                        <p className="contact-address">{userData.company}</p>
                        {userData.address && (
                          <>
                            <p className="contact-street">
                              {userData.address.street} {userData.address.postBox}
                            </p>
                            <p className="contact-city">
                              {userData.address.country} {userData.address.postalCode} {userData.address.city}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid style={{ marginBottom: 10 }}>
                        <SelectComponent
                          label={`${t('Tickets.requestType')} *`}
                          options={[
                            { value: 'Incident', name: 'Report an incident' },
                            { value: 'Task', name: 'Ask for a Service Request or a task' },
                            { value: 'Question', name: 'Ask for Information' }
                          ]}
                          onChange={e => {
                            updatePostData('requestType', e.target.value);
                          }}
                          value={postData.requestType || ''}
                          error={validations.requestType || null}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={12}>
                      <Grid style={{ marginBottom: 10 }}>
                        <SelectComponent
                          label={`${t('Tickets.component')} *`}
                          options={componentData}
                          onChange={e => updatePostData('component', e.target.value)}
                          value={postData.component || ''}
                          error={validations.component || null}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {postData.component === 'Device' && (
                    <>
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid style={{ marginBottom: 10 }}>
                            <SelectComponent
                              label={`${t('Tickets.category')} *`}
                              options={[
                                { value: 'Hardware', name: 'Hardware' },
                                { value: 'Software', name: 'Functional or Technical error' }
                              ]}
                              onChange={e => updatePostData('tollingCategory', e.target.value)}
                              value={postData.tollingCategory || ''}
                              error={validations.tollingCategory || null}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid style={{ marginBottom: 10 }}>
                        <TextField
                          className="create-ticket-text-field"
                          label={`${t('Tickets.title')} *`}
                          onChange={e => updatePostData('title', e.target.value)}
                          value={postData.title || ''}
                          error={validations.title || null}
                          // helperText={postData.title ? `${postData.title.length}/200` : ``}
                          inputProps={{ maxLength: 200 }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid style={{ marginBottom: 30 }}>
                      <TextField
                        className="create-ticket-textarea"
                        multiline
                        label={`${t('Tickets.description')} *`}
                        onChange={e => updatePostData('description', e.target.value)}
                        value={postData.description || ''}
                        variant="outlined"
                        minRows={4}
                        error={validations.description || null}
                        // helperText={postData.description ? `${postData.description.length}/100000` : ``}
                        inputProps={{ maxLength: 100000 }}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    // wrap="nowrap"
                    className="create-ticket-selections"
                  >
                    <Grid item xs={12} md={4}>
                      <Grid style={{ marginBottom: 10 }}>
                        <TextField
                          type="date"
                          className="create-ticket-text-field create-ticket-text-field-date"
                          label={`${t('Tickets.incidentDateOfOccurrence')}`}
                          onChange={e => updatePostData('dateOfOccurence', e.target.value)}
                          value={postData.dateOfOccurence || ''}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Grid style={{ marginBottom: 10 }}>
                        <TextField
                          type="time"
                          className="create-ticket-text-field create-ticket-text-field-date"
                          label={`${t('Tickets.incidentTimeOfOccurrence')}`}
                          onChange={e => updatePostData('timeOfOccurence', e.target.value)}
                          value={postData.timeOfOccurence || ''}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Grid style={{ marginBottom: 10 }}>
                        <TextField
                          className="create-ticket-text-field"
                          label={`${t('Tickets.serialNumber')} *`}
                          onChange={e => updatePostData('deviceserialnumber', e.target.value)}
                          value={postData.deviceserialnumber || ''}
                          error={validations.deviceserialnumber || null}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    // wrap="nowrap"
                    className="create-ticket-selections"
                  >
                    <Grid item xs={12} md={4}>
                      <Grid style={{ marginBottom: 10 }}>
                        <SelectComponent
                          label={`${t('Tickets.impact')} *`}
                          options={impactData}
                          onChange={e => updatePostData('impact', e.target.value)}
                          value={postData.impact}
                          error={validations.impact || null}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Grid style={{ marginBottom: 10 }}>
                        <SelectComponent
                          label={`${t('Tickets.urgency')} *`}
                          options={urgencyData}
                          onChange={e => updatePostData('urgency', e.target.value)}
                          value={postData.urgency}
                          error={validations.urgency || null}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Grid style={{ marginBottom: 10 }}>
                        <TextField
                          className="create-ticket-text-field"
                          label={`${t('Tickets.firmwareVersion')}`}
                          onChange={e => updatePostData('softwareVersion', e.target.value)}
                          value={postData.softwareVersion || ''}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid style={{ marginTop: '20px' }}>
                    <UploadField
                      dataValue={postData.attachments}
                      accept={`application/pdf, image/*`}
                      type={`screenshot`}
                      error={validations.attachments}
                      filesLimit={MAX_NUMBER_OF_FILES}
                      setValidations={setValidations}
                      setPostData={setPostData}
                      postData={postData}
                      fileKey={fileKey}
                    />
                  </Grid>
                </Box>
                <div className="create-ticket-btns">
                  <button disabled={updateTicketLoading} className="btn-restart" onClick={restartProcess}>
                    {t('Tickets.restartProcess')}
                  </button>
                  <Button
                    disabled={updateTicketLoading}
                    borderOnly
                    onClick={handleSubmit}
                    additionalClassName="btn-submit"
                  >
                    {updateTicketLoading ? t('personalData.loading') : t('Tickets.submit')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <CancelCreateTicketModal
        onClose={() => setCancelModalOpen(false)}
        open={cancelModalOpen}
        closeNewTicket={closeNewTicket}
      />
    </>
  );
}
